/**
 * CalendarToolbar Component
 *
 * Custom toolbar for the calendar with view controls and filters
 */

import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

function CalendarToolbar({
  date,
  view,
  views,
  label,
  onNavigate,
  onView,
  statusFilter,
  onStatusFilterChange,
}) {
  const navigate = (action) => {
    onNavigate(action);
  };

  const viewNamesGroup = (view) => {
    const messages = {
      month: "Month",
      week: "Week",
      day: "Day",
      agenda: "Agenda",
    };
    return messages[view] || view;
  };

  return (
    <MDBox
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      gap={{ xs: 1.5, sm: 2 }}
      mb={3}
      p={{ xs: 1.5, sm: 2 }}
      sx={{
        backgroundColor: ({ palette }) => palette.background.card,
        borderRadius: "12px",
        boxShadow: ({ boxShadows }) => boxShadows.md,
      }}
    >
      {/* Navigation Controls */}
      <MDBox display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }}>
        <MDButton
          size="small"
          color="dark"
          variant="outlined"
          onClick={() => navigate("TODAY")}
          sx={{
            minWidth: { xs: "60px", sm: "auto" },
            minHeight: { xs: "36px", sm: "32px" },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            px: { xs: 1, sm: 2 },
          }}
        >
          Today
        </MDButton>
        <MDButton
          size="small"
          color="dark"
          variant="text"
          iconOnly
          onClick={() => navigate("PREV")}
          sx={{ minWidth: "36px", minHeight: "36px" }}
        >
          <Icon>chevron_left</Icon>
        </MDButton>
        <MDButton
          size="small"
          color="dark"
          variant="text"
          iconOnly
          onClick={() => navigate("NEXT")}
          sx={{ minWidth: "36px", minHeight: "36px" }}
        >
          <Icon>chevron_right</Icon>
        </MDButton>
      </MDBox>

      {/* Calendar Label */}
      <MDBox
        sx={{
          order: { xs: -1, md: 0 },
          width: { xs: "100%", md: "auto" },
          textAlign: { xs: "center", md: "left" },
        }}
      >
        <MDTypography
          variant="h5"
          fontWeight="medium"
          sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
        >
          {label}
        </MDTypography>
      </MDBox>

      {/* View Controls and Filters */}
      <MDBox
        display="flex"
        alignItems="center"
        gap={{ xs: 1, sm: 2 }}
        flexWrap="wrap"
        justifyContent={{ xs: "center", sm: "flex-end" }}
        sx={{ width: { xs: "100%", md: "auto" } }}
      >
        {/* Status Filter */}
        <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 } }}>
          <InputLabel sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
            Filter by Status
          </InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            label="Filter by Status"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              minHeight: { xs: "36px", sm: "40px" },
            }}
          >
            <MenuItem value="all">All Posts</MenuItem>
            <MenuItem value="Posted">Posted</MenuItem>
            <MenuItem value="Pending Approval">Pending Approval</MenuItem>
            <MenuItem value="Good to go">Good to go</MenuItem>
            <MenuItem value="Rejected">Rejected</MenuItem>
          </Select>
        </FormControl>

        {/* View Buttons */}
        <MDBox display="flex" gap={0.5}>
          {views.map((viewName) => (
            <MDButton
              key={viewName}
              size="small"
              color={view === viewName ? "info" : "dark"}
              variant={view === viewName ? "gradient" : "outlined"}
              onClick={() => onView(viewName)}
              sx={{
                minWidth: { xs: "auto", sm: "60px" },
                minHeight: { xs: "32px", sm: "36px" },
                px: { xs: 1, sm: 2 },
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
              }}
            >
              {viewNamesGroup(viewName)}
            </MDButton>
          ))}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

CalendarToolbar.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  view: PropTypes.string.isRequired,
  views: PropTypes.arrayOf(PropTypes.string).isRequired,
  label: PropTypes.string.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  statusFilter: PropTypes.string.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
};

export default CalendarToolbar;
